const SITE_NAME    = 'Amaken';
const SITE_OG_IMG  = 'https://amakenapp.com/amaken-fav.png';

/**
 * Default meta per route × language.
 * Values mirror what is already in index.html so the page never goes "blank".
 */
const ROUTE_META = {
  home: {
    EN: {
      title: 'Best Restaurants & Dining Offers in Doha, Qatar | Amaken',
      description: 'Discover the best dining and restaurants in Doha, Qatar. Browse ratings, explore menus, and book a table now at places open today - exclusive offers on Amaken.',
      keywords: 'restaurants in Qatar, restaurant offers, restaurant events, best dining Qatar, breakfast restaurants Qatar, lunch restaurants Qatar, dinner restaurants Qatar, Ramadan restaurants Qatar, halal restaurants Qatar, fine dining Qatar, casual dining Qatar',
    },
    AR: {
      title: 'اكتشف أفضل أماكن الطعام والعروض الحصرية في الدوحة | أماكن',
      description: 'هل تبحث عن أفضل تجارب تناول الطعام في الدوحة؟ يساعدك أماكن على استكشاف أفضل المطاعم والمقاهي واكتشاف العروض الحصرية.',
      keywords: 'مطاعم قطر، عروض مطاعم، أفضل مطاعم الدوحة، حجز مطعم، فعاليات مطاعم',
    },
  },
  places: {
    EN: {
      title: 'Restaurants in Doha, Qatar - Browse & Book | Amaken',
      description: 'Browse all restaurants in Doha, Qatar. Filter by cuisine, location, vibes, and amenities. Find your perfect dining spot and book a table on Amaken.',
      keywords: 'restaurants Qatar, dining Doha, restaurant list Qatar, book restaurant Doha',
    },
    AR: {
      title: 'مطاعم الدوحة، قطر - تصفح واحجز | أماكن',
      description: 'تصفح جميع المطاعم في الدوحة، قطر. فلتر حسب المطبخ والموقع والأجواء والمرافق.',
      keywords: 'مطاعم قطر، مطاعم الدوحة، حجز مطعم، قائمة مطاعم',
    },
  },
  offers: {
    EN: {
      title: 'Exclusive Restaurant Offers & Dining Deals in Qatar | Amaken',
      description: 'Discover the best restaurant offers, discounts, and dining deals in Qatar. Save on your favourite restaurants with exclusive offers on Amaken.',
      keywords: 'restaurant offers Qatar, dining deals Doha, restaurant discounts Qatar',
    },
    AR: {
      title: 'عروض وصفقات المطاعم الحصرية في قطر | أماكن',
      description: 'اكتشف أفضل عروض المطاعم والخصومات وصفقات تناول الطعام في قطر.',
      keywords: 'عروض مطاعم قطر، تخفيضات مطاعم الدوحة، صفقات طعام',
    },
  },
  events: {
    EN: {
      title: 'Restaurant Events & Dining Experiences in Qatar | Amaken',
      description: 'Find the best restaurant events, themed dinners, and special dining experiences in Qatar. Book your spot on Amaken.',
      keywords: 'restaurant events Qatar, dining events Doha, special events restaurants',
    },
    AR: {
      title: 'فعاليات المطاعم وتجارب الطعام في قطر | أماكن',
      description: 'اعثر على أفضل فعاليات المطاعم وتجارب الطعام الخاصة في قطر.',
      keywords: 'فعاليات مطاعم قطر، فعاليات الدوحة',
    },
  },
  'not-found': {
    EN: {
      title: '404 - Page Not Found | Amaken',
      description: "The page you're looking for doesn't exist or may have been moved. Browse restaurants, offers, and events on Amaken.",
      keywords: '',
    },
    AR: {
      title: '404 - الصفحة غير موجودة | أماكن',
      description: 'الصفحة التي تبحث عنها غير موجودة أو ربما تم نقلها. تصفح المطاعم والعروض والفعاليات على أماكن.',
      keywords: '',
    },
  },
};

/**
 * Called in router.afterEach for every route that is NOT place-detail.
 * Resets title, description, keywords, OG, Twitter to sensible per-route defaults.
 * Removes any place-specific JSON-LD left from a previous restaurant page.
 */
export function setRouteMeta(routeName, lang = 'EN') {
  const langKey  = lang.toUpperCase() === 'AR' ? 'AR' : 'EN';
  const routeKey = routeName || 'home';
  const meta     = (ROUTE_META[routeKey] || ROUTE_META.home)[langKey];

  setPageMeta({
    title:       meta.title,
    description: meta.description,
    keywords:    meta.keywords || '',
    image:       SITE_OG_IMG,
    url:         typeof window !== 'undefined' ? window.location.href : '',
    lang:        langKey.toLowerCase(),
    robots:      routeKey === 'not-found' ? 'noindex, follow' : 'index, follow',
  });

  removeJsonLd();
}

function upsertMeta(attr, value, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${value}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, value);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

/** Set (or update) the <link rel="canonical"> tag to the current path (no query string). */
function setCanonicalLink() {
  if (typeof window === 'undefined') return;
  const path = window.location.pathname;
  const pathNorm = path === '/' ? '/' : path.replace(/\/+$/, '');
  const href = window.location.origin + pathNorm;

  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

/**
 * Set all page-level meta tags (title, description, OG, Twitter Card).
 * Safe to call multiple times — updates existing tags in-place.
 */
export function setPageMeta({ title, description, image = '', url = '', keywords = '', lang = 'en', robots = 'index, follow' }) {
  document.title = title;
  document.documentElement.setAttribute('lang', lang.toLowerCase());

  upsertMeta('name', 'description', description);
  if (keywords) upsertMeta('name', 'keywords', keywords);
  upsertMeta('name', 'robots', robots);
  setCanonicalLink();

  // Open Graph
  upsertMeta('property', 'og:title', title);
  upsertMeta('property', 'og:description', description);
  upsertMeta('property', 'og:image', image);
  upsertMeta('property', 'og:url', url || window.location.href);
  upsertMeta('property', 'og:type', 'restaurant');
  upsertMeta('property', 'og:site_name', SITE_NAME);

  // Twitter Card
  upsertMeta('name', 'twitter:card', 'summary_large_image');
  upsertMeta('name', 'twitter:title', title);
  upsertMeta('name', 'twitter:description', description);
  if (image) upsertMeta('name', 'twitter:image', image);
}

/** Inject (or replace) a JSON-LD <script> tag into <head>. */
export function injectJsonLd(schema) {
  removeJsonLd();
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.setAttribute('data-schema', 'restaurant');
  script.textContent = JSON.stringify(schema);
  document.head.appendChild(script);
}

/** Remove the injected JSON-LD tag — call in beforeUnmount to avoid leaks. */
export function removeJsonLd() {
  const el = document.head.querySelector('script[data-schema="restaurant"]');
  if (el) el.remove();
}
